import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link, NavLink } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
    GraduationCap,
    BookOpen,
    LogIn,
    Moon,
    Sun,
    FlaskConical,
    Waypoints,
} from 'lucide-react';
import { clsx } from 'clsx';
import { SidebarProvider, useSidebarCollapse } from '../contexts/SidebarContext';
import { isFullBleedRoute } from '../utils/layoutRoutes';
import { OutrisSiteFooter } from './OutrisSiteFooter';
import {
    ensureGuestDemo,
    exitGuestDemo,
    isGuestDemoPath,
} from '../utils/guestDemo';
import { hasActiveSession } from '../utils/session';
import { EdgeCollapseTab } from './workbench/EdgeCollapseTab';
import OutrisFaviconDark from '../assets/outris-square-dark.svg';
import OutrisFaviconLight from '../assets/outris-square-light.svg';

const GUEST_NAV = [
    { icon: GraduationCap, label: 'Demos', path: '/demo' },
    { icon: Waypoints, label: 'Fusion', path: '/demo/fusion' },
    { icon: BookOpen, label: 'Documentation', path: '/demo/documentation' },
] as const;

function GuestThemeToggle() {
    const [isDark, setIsDark] = React.useState(() =>
        typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : true,
    );

    const toggle = () => {
        setIsDark((prev) => {
            const next = !prev;
            if (next) {
                document.documentElement.classList.add('dark');
                localStorage.setItem('theme', 'dark');
            } else {
                document.documentElement.classList.remove('dark');
                localStorage.setItem('theme', 'light');
            }
            return next;
        });
    };

    return (
        <button
            type="button"
            onClick={toggle}
            className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            data-testid="guest-theme-toggle"
        >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
    );
}

function GuestHeader() {
    const navigate = useNavigate();

    return (
        <header
            className="shrink-0 h-12 border-b border-border bg-card flex items-center justify-between px-4 gap-3"
            data-testid="guest-demo-header"
        >
            <div className="flex items-center gap-2 min-w-0">
                <FlaskConical className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm font-semibold text-foreground truncate">CryptoFlow Demo</span>
                <span className="hidden sm:inline text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/30">
                    Guest
                </span>
            </div>
            <div className="flex items-center gap-1.5">
                <GuestThemeToggle />
                <button
                    type="button"
                    onClick={() => {
                        exitGuestDemo();
                        navigate('/login', { state: { from: { pathname: '/demo' } } });
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-brand-gold-hover"
                    data-testid="guest-sign-in"
                >
                    <LogIn className="w-3.5 h-3.5" />
                    Sign In
                </button>
            </div>
        </header>
    );
}

function GuestBanner() {
    const navigate = useNavigate();
    return (
        <div
            className="shrink-0 px-4 py-2 text-xs border-b border-amber-500/30 bg-amber-500/10 text-amber-950 dark:text-amber-100 flex flex-wrap items-center justify-between gap-2"
            data-testid="guest-demo-banner"
            role="status"
        >
            <span>
                Demo mode — Mock/Cached Workbench graphs and synthetic Fusion Canvas only. Live chain APIs, cases, and investigations require Sign In.
            </span>
            <button
                type="button"
                className="font-semibold underline underline-offset-2 hover:no-underline shrink-0"
                onClick={() => {
                    exitGuestDemo();
                    navigate('/login');
                }}
            >
                Sign in for full tools
            </button>
        </div>
    );
}

function GuestSidebar() {
    const { isCollapsed, toggleSidebar } = useSidebarCollapse();
    const location = useLocation();

    return (
        <div
            className={clsx(
                'relative z-20 shrink-0 hidden lg:flex flex-col min-h-0 self-stretch transition-[width] duration-200 ease-in-out',
                isCollapsed ? 'w-16' : 'w-56',
            )}
        >
            <aside className="flex flex-col flex-1 min-h-0 overflow-hidden border-r border-border bg-card">
                <div className={clsx('flex-1 min-h-0 overflow-y-auto', isCollapsed ? 'px-2 py-3' : 'p-4')}>
                    <nav className="space-y-1" aria-label="Demo navigation">
                        {GUEST_NAV.map((item) => {
                            const active =
                                item.path === '/demo'
                                    ? location.pathname === '/demo'
                                    : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    end={item.path === '/demo'}
                                    title={isCollapsed ? item.label : undefined}
                                    className={clsx(
                                        'flex items-center rounded-md text-sm font-medium transition-colors min-h-[44px]',
                                        isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2',
                                        active
                                            ? 'bg-brand-cream/40 dark:bg-primary/10 text-foreground'
                                            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                                    )}
                                    data-testid={`guest-nav-${item.label.toLowerCase()}`}
                                >
                                    <item.icon className={clsx('shrink-0', isCollapsed ? 'w-5 h-5' : 'w-4 h-4')} />
                                    {!isCollapsed && item.label}
                                </NavLink>
                            );
                        })}
                    </nav>
                </div>
                <div className={clsx(
                    'mt-auto shrink-0 border-t border-primary/30 flex items-center justify-center',
                    isCollapsed ? 'px-2 py-3' : 'p-4',
                )}>
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                        <img src={OutrisFaviconLight} alt="Outris" className={clsx('block dark:hidden', isCollapsed ? 'h-7 w-7' : 'h-6 w-6')} />
                        <img src={OutrisFaviconDark} alt="Outris" className={clsx('hidden dark:block', isCollapsed ? 'h-7 w-7' : 'h-6 w-6')} />
                        {!isCollapsed && (
                            <span>Powered by <span className="font-medium text-foreground/80">Outris</span></span>
                        )}
                    </div>
                </div>
            </aside>
            <EdgeCollapseTab
                side="right"
                direction={isCollapsed ? 'expand' : 'collapse'}
                onClick={toggleSidebar}
                label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            />
        </div>
    );
}

/**
 * Public shell for unauthenticated demo exploration (/demo/*).
 * Logged-in officers hitting /demo are sent to the authenticated demos library.
 */
export const GuestDemoLayout: React.FC = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const fullBleed = isFullBleedRoute(pathname);

    useEffect(() => {
        if (hasActiveSession()) {
            const rest = pathname.replace(/^\/demo/, '') || '';
            if (rest.startsWith('/workbench')) {
                navigate(`/workbench${window.location.search}`, { replace: true });
            } else if (rest.startsWith('/fusion')) {
                navigate(`/fusion${window.location.search}`, { replace: true });
            } else if (rest.startsWith('/documentation')) {
                navigate('/documentation', { replace: true });
            } else {
                navigate('/demos', { replace: true });
            }
            return;
        }
        ensureGuestDemo();
    }, [navigate, pathname]);

    if (!isGuestDemoPath(pathname)) {
        return null;
    }

    if (hasActiveSession()) {
        return <div className="min-h-screen bg-background" data-testid="guest-redirecting" />;
    }

    return (
        <SidebarProvider>
            <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden" data-testid="guest-demo-layout">
                <GuestHeader />
                <GuestBanner />
                <Toaster position="top-right" richColors closeButton />
                <div className="flex flex-1 min-h-0 h-0 overflow-hidden">
                    <GuestSidebar />
                    <main
                        id="main-content"
                        className="relative flex flex-1 min-h-0 min-w-0 overflow-hidden bg-background"
                        tabIndex={-1}
                    >
                        {fullBleed ? (
                            <div className="absolute inset-0 flex flex-col min-h-0 overflow-hidden">
                                <Outlet />
                            </div>
                        ) : (
                            <div className="absolute inset-0 overflow-y-auto overflow-x-hidden styled-scrollbar page-scrollbar overscroll-y-contain">
                                <div className="min-h-full flex flex-col">
                                    <div className="flex-1">
                                        <Outlet />
                                    </div>
                                    <OutrisSiteFooter />
                                </div>
                            </div>
                        )}
                    </main>
                </div>
                <div className="sr-only">
                    <Link to="/login">Sign in</Link>
                </div>
            </div>
        </SidebarProvider>
    );
};
